import React from 'react';
import { ArrowUpRight, Check } from '@phosphor-icons/react';
import { SectionHeading } from './BrandMark';
import { PORTFOLIO_SITES } from './content';

function SiteThumbnail({ site }) {
  if (site.image) {
    return (
      <img
        src={site.image}
        alt={`${site.name} — ${site.domain}`}
        loading="lazy"
        width="1200"
        height="750"
        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
      />
    );
  }
  // No screenshot yet: a branded tile with the site's own name, not a fake preview.
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center px-6 text-center"
      style={{ background: `linear-gradient(135deg, ${site.accent} 0%, #0A0A0A 120%)` }}
    >
      <span className="font-display text-2xl font-semibold leading-tight text-[#F8F6F0] sm:text-3xl">{site.name}</span>
      <span className="mt-3 font-landing text-xs tracking-[0.2em] text-[#F8F6F0]/70">{site.domain.toUpperCase()}</span>
    </div>
  );
}

function SiteCard({ site, text, visitLabel, delay }) {
  return (
    <a
      href={site.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-4 focus-visible:ring-offset-[#F1EDE3]"
      data-reveal
      style={{ '--reveal-delay': `${delay}ms` }}
    >
      <div className="overflow-hidden border border-[#0A0A0A]/10 bg-white shadow-[0_24px_60px_-34px_rgba(10,10,10,0.5)] transition-shadow duration-500 group-hover:shadow-[0_34px_70px_-30px_rgba(10,10,10,0.55)]">
        <div className="flex items-center gap-3 border-b border-[#0A0A0A]/10 bg-[#F8F6F0] px-4 py-2.5" aria-hidden="true">
          <span className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#0A0A0A]/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#0A0A0A]/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#0A0A0A]/15" />
          </span>
          <span className="truncate rounded-sm bg-white px-3 py-1 font-landing text-[0.68rem] text-[#4A4740]">{site.domain}</span>
        </div>
        <div className="aspect-[16/10] overflow-hidden bg-[#0A0A0A]">
          <SiteThumbnail site={site} />
        </div>
      </div>
      <div className="mt-6">
        <p className="font-landing text-[0.68rem] font-semibold uppercase tracking-[0.26em] text-[#8A6A12]">{text.type}</p>
        <h3 className="mt-2 flex items-center gap-2 font-display text-2xl font-semibold text-[#0A0A0A]">
          {site.name}
          <ArrowUpRight size={20} weight="bold" className="text-[#8A6A12] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
        </h3>
        <p className="mt-3 font-landing text-[0.92rem] leading-relaxed text-[#4A4740]">{text.description}</p>
        <span className="mt-4 inline-block border-b border-[#D4AF37] font-landing text-xs font-semibold uppercase tracking-[0.18em] text-[#0A0A0A]">{visitLabel}</span>
      </div>
    </a>
  );
}

export function SitesSection({ copy }) {
  const section = copy.sites;
  return (
    <section id="sites" className="scroll-mt-16 bg-[#F1EDE3] py-24 lg:py-32" aria-labelledby="sites-title">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow={section.eyebrow} title={section.title} subtitle={section.subtitle} />
        <h2 id="sites-title" className="sr-only">{section.title}</h2>

        <div className="mt-16 grid gap-12 md:grid-cols-2 lg:grid-cols-3">
          {PORTFOLIO_SITES.map((site, index) => (
            <SiteCard key={site.id} site={site} text={section.items[site.id]} visitLabel={section.visit} delay={index * 110} />
          ))}
        </div>

        <div className="mt-20 border-t-2 border-[#D4AF37] bg-white p-8 sm:p-10" data-reveal>
          <h3 className="font-display text-2xl font-semibold text-[#0A0A0A]">{section.includesTitle}</h3>
          <ul className="mt-6 grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {section.includes.map((item) => (
              <li key={item} className="flex gap-3 font-landing text-[0.95rem] text-[#2B2924]">
                <Check size={18} weight="bold" className="mt-0.5 shrink-0 text-[#8A6A12]" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
